<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import CloseIcon from '@/js/core/components/icons/CloseIcon.vue';
import PersonProfileMetaList from '@/js/core/components/pages/person-profile-modal/PersonProfileMetaList.vue';
import PersonProfileBio from '@/js/core/components/pages/person-profile-modal/PersonProfileBio.vue';
import NavigationPanel from '@/js/core/components/parts/navigation-panel/NavigationPanel.vue';
import { loadPublicProfile } from '@/js/core/utils/profile-api';
import { toPersonShape } from '@/js/core/utils/profile-shape';

const props = defineProps({
  profileUuid: {
    type: String,
    required: true,
  },
  panelId: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['close']);

const person = ref(null);
const isLoading = ref(true);
const closeButtonRef = ref(null);

const close = reason => emit('close', reason);

const handleKeydown = event => {
  if (event.key !== 'Escape') return;

  event.stopPropagation();
  event.preventDefault();
  close('escape');
};

const handleOutsideClick = event => {
  // Use composedPath which prevents the Read All button from triggering the Modal close
  const path = event.composedPath();
  const isInside = path.some(
    node =>
      node instanceof Element &&
      (node.classList.contains('person-profile-modal__wrapper') ||
        node.matches('[data-person-trigger]'))
  );

  if (isInside) return;

  close('outside-click');
};

let abortController = null;

onMounted(async () => {
  document.addEventListener('keydown', handleKeydown, true);
  document.addEventListener('click', handleOutsideClick);
  closeButtonRef.value?.focus();

  abortController = new AbortController();

  try {
    const response = await loadPublicProfile(props.profileUuid, {
      signal: abortController.signal,
    });

    person.value = toPersonShape(response?.data ?? {});
  } catch (error) {
    // Selecting another person remounts this component and aborts the old request;
    // emitting close there would clear the new selection.
    if (error.name === 'AbortError') return;

    close('error');
  } finally {
    isLoading.value = false;
  }
});

onBeforeUnmount(() => {
  abortController?.abort();
  document.removeEventListener('keydown', handleKeydown, true);
  document.removeEventListener('click', handleOutsideClick);
});

const personName = computed(
  () =>
    person.value?.displayName ||
    [person.value?.firstName, person.value?.lastName].filter(Boolean).join(' ')
);
const categoryLabel = computed(() => person.value?.audienceLabel || 'Profile');
const hasBio = computed(() => !!person.value?.bio);
const hasDescription = computed(() => !!person.value?.description);
</script>

<template>
  <div
    :id="panelId"
    class="person-profile-modal"
    role="dialog"
    :aria-label="personName || 'Profile'"
  >
    <div class="person-profile-modal__backdrop" @click="close('outside-click')"></div>
    <div class="person-profile-modal__wrapper">
      <div class="person-profile-modal__container">
        <div class="person-profile-modal__scroll-area" aria-live="polite" :aria-busy="isLoading">
          <template v-if="person">
            <div class="person-profile-modal__header-top">
              <span class="person-profile-modal__category">{{ categoryLabel }}</span>
            </div>
            <h1 class="person-profile-modal__title">{{ personName }}</h1>
            <div class="person-profile-modal__content">
              <PersonProfileMetaList :person="person" />
              <PersonProfileBio v-if="hasDescription" :bio="person.description" html />
              <PersonProfileBio v-if="hasBio" :bio="person.bio" />
            </div>
          </template>

          <div v-else-if="isLoading" class="person-profile-modal__header-top">
            <span class="person-profile-modal__category">Loading…</span>
          </div>

          <footer class="person-profile-modal__footer">
            <div class="person-profile-modal__footer-inner">
              <div class="person-profile-navbar__wrapper">
                <NavigationPanel />
              </div>
            </div>
          </footer>
        </div>
        <div class="person-profile-modal__close-container">
          <button
            ref="closeButtonRef"
            type="button"
            class="person-profile-modal__close"
            aria-label="Close profile"
            @click="close('close-button')"
          >
            <div class="person-profile-modal__close-wrapper"><CloseIcon /></div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
